/**
 * wasmThumbnail — LAST-RESORT client-side thumbnail generation via ffmpeg.wasm.
 *
 * Order of the overall thumbnail strategy:
 *   1. native <video> + canvas   (fast, free — handles ~all common codecs)
 *   2. server FFmpeg fallback     (handles codecs the browser can't decode)
 *   3. THIS (ffmpeg.wasm)         (only when 1 AND 2 both fail)
 *
 * Everything here is lazy: the ~30MB wasm core is fetched from a CDN the first
 * time it's actually needed (never on app load, never in the main bundle), and
 * the single-thread core is used so NO cross-origin-isolation (COOP/COEP) headers
 * are required. Every path is wrapped so any failure resolves to null — the caller
 * then shows the placeholder, exactly as before. It can never break other flows.
 */

// Single-thread core (no SharedArrayBuffer/threads → no special headers needed).
const CORE_BASE = 'https://unpkg.com/@ffmpeg/core@0.12.10/dist/umd';

let ffmpegPromise = null;

async function getFFmpeg() {
  if (ffmpegPromise) return ffmpegPromise;
  ffmpegPromise = (async () => {
    const [{ FFmpeg }, { toBlobURL }] = await Promise.all([
      import('@ffmpeg/ffmpeg'),
      import('@ffmpeg/util'),
    ]);
    const ffmpeg = new FFmpeg();
    await ffmpeg.load({
      coreURL: await toBlobURL(`${CORE_BASE}/ffmpeg-core.js`, 'text/javascript'),
      wasmURL: await toBlobURL(`${CORE_BASE}/ffmpeg-core.wasm`, 'application/wasm'),
    });
    return ffmpeg;
  })().catch((e) => {
    // Reset so a later attempt can retry a failed load.
    ffmpegPromise = null;
    throw e;
  });
  return ffmpegPromise;
}

function blobToDataUrl(blob) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(typeof reader.result === 'string' ? reader.result : null);
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(blob);
  });
}

// Read the video duration (seconds) from ffmpeg's own logs (no separate ffprobe).
async function probeDuration(ffmpeg, inName) {
  let logBuf = '';
  const onLog = ({ message }) => { logBuf += `${message}\n`; };
  try { ffmpeg.on('log', onLog); } catch (e) { /* older API */ }
  // Running with no output errors out, but ffmpeg still prints "Duration: ...".
  try { await ffmpeg.exec(['-hide_banner', '-i', inName]); } catch (e) { /* expected */ }
  try { ffmpeg.off('log', onLog); } catch (e) { /* ignore */ }
  const m = logBuf.match(/Duration:\s*(\d+):(\d+):(\d+(?:\.\d+)?)/);
  if (!m) return 0;
  return (Number(m[1]) * 3600) + (Number(m[2]) * 60) + parseFloat(m[3]);
}

async function extractFrame(ffmpeg, inName, seek, outName) {
  try {
    await ffmpeg.exec([
      '-ss', String(seek),
      '-i', inName,
      '-frames:v', '1',
      '-vf', 'scale=640:-2',
      outName,
    ]);
    const data = await ffmpeg.readFile(outName);
    try { await ffmpeg.deleteFile(outName); } catch (e) { /* ignore */ }
    if (!data || !data.length) return null;
    return await blobToDataUrl(new Blob([data], { type: 'image/png' }));
  } catch (e) {
    return null;
  }
}

/**
 * Decode a (usually small) video Blob and return its FIRST frame as a data URL,
 * without probing duration or seeking. This is the reliable path for HLS media
 * segments (`.ts` / `.m4s`), where duration probing is flaky and mid-segment
 * seeks often fail. Returns null on any failure.
 */
export async function generateFirstFrameWithWasm(file) {
  try {
    if (!file || typeof file.arrayBuffer !== 'function') return null;
    const { fetchFile } = await import('@ffmpeg/util');
    const ffmpeg = await getFFmpeg();
    const inName = 'wasm_seg_in';
    await ffmpeg.writeFile(inName, await fetchFile(file));
    // No -ss: decode from the start and take the first frame.
    const url = await extractFrame(ffmpeg, inName, 0, 'wasm_seg_out.png');
    try { await ffmpeg.deleteFile(inName); } catch (e) { /* ignore */ }
    return url;
  } catch (e) {
    console.warn('[wasmThumb] first-frame decode failed:', e);
    return null;
  }
}

/**
 * Generate up to `count` evenly-spaced thumbnails from a video File entirely in
 * the browser (mirrors the native/local flow which produces 6 selectable frames).
 * Returns an array of `data:image/png;base64,...` URLs (possibly empty on failure).
 */
export async function generateThumbnailsWithWasm(file, options = {}) {
  const count = options.count || 6;
  try {
    if (!file || typeof file.arrayBuffer !== 'function') return [];
    const { fetchFile } = await import('@ffmpeg/util');
    const ffmpeg = await getFFmpeg();

    const inName = 'wasm_thumb_in';
    await ffmpeg.writeFile(inName, await fetchFile(file));

    const duration = await probeDuration(ffmpeg, inName);
    const urls = [];
    if (duration > 0) {
      // Same spacing as the local capture: interval = duration / (count + 1),
      // frames at interval*1 .. interval*count.
      const interval = duration / (count + 1);
      for (let i = 1; i <= count; i += 1) {
        // eslint-disable-next-line no-await-in-loop
        const url = await extractFrame(ffmpeg, inName, interval * i, `wasm_thumb_${i}.png`);
        if (url) urls.push(url);
      }
    } else {
      // Unknown duration → at least grab one early frame.
      const url = await extractFrame(ffmpeg, inName, 1, 'wasm_thumb_1.png');
      if (url) urls.push(url);
    }

    try { await ffmpeg.deleteFile(inName); } catch (e) { /* ignore */ }
    return urls;
  } catch (e) {
    return [];
  }
}

/**
 * Single-thumbnail convenience wrapper — returns one data URL or null.
 */
export async function generateThumbnailWithWasm(file, options = {}) {
  const urls = await generateThumbnailsWithWasm(file, { ...options, count: 1 });
  return urls[0] || null;
}

export default generateThumbnailWithWasm;
